import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSubscription } from '../context/SubscriptionContext';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const TrialEnd = () => {
  const { t, lang } = useLanguage();
  const sub = useSubscription();
  
  // ============ STATES ============
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingStep, setBillingStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dynamic plan limits from /api/plans/all (admin-editable source of truth).
  // Falls back to legacy hardcoded numbers until the API responds.
  const [apiPlans, setApiPlans] = useState({
    starter: { credits: 400, social: 1, website: 1, videos: 0, price: 79 },
    growth:  { credits: 950, social: 3, website: 3, videos: 1, price: 199 },
  });
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/plans/all`);
        const list = Array.isArray(r.data) ? r.data : [];
        const next = { ...apiPlans };
        for (const p of list) {
          const k = String(p.name || '').toLowerCase();
          next[k] = {
            credits: Number(p.limits?.credits_monthly || 0),
            social:  Number(p.limits?.connected_channels_social || 0),
            website: Number(p.limits?.connected_channels_website || 0),
            videos:  Number(p.limits?.videos_monthly || 0),
            price:   Number(p.price_euros || 0),
          };
        }
        setApiPlans(next);
      } catch { /* keep fallback */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fmtN = (n) => Number(n || 0).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US');
  
  const [formData, setFormData] = useState({
    companyName: '',
    companyType: '',
    taxId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    zip: '',
    country: 'DE',
  });

  // ============ DATA ============
  const plans = {
    monthly: {
      starter: { 
        name: 'Starter', 
        price: 99, 
        description: lang === 'de' ? 'Perfekt für den Anfang' : 'Perfect to get started' 
      },
      growth: { 
        name: 'Growth', 
        price: 229, 
        description: lang === 'de' ? 'Für schnelles Wachstum' : 'For rapid growth' 
      },
    },
    yearly: {
      starter: { 
        name: 'Starter', 
        price: 79, 
        description: lang === 'de' ? 'Perfekt für den Anfang' : 'Perfect to get started' 
      },
      growth: { 
        name: 'Growth', 
        price: 199, 
        description: lang === 'de' ? 'Für schnelles Wachstum' : 'For rapid growth' 
      },
    },
  };

  const features = {
    starter: lang === 'de' ? [
      '1 Marke',
      'Umfassende Markenanalyse als Grundlage jedes Content Pieces',
      `${fmtN(apiPlans.starter.credits)} Credits für die Kreation und Produktion von Content Pieces: Jedes Content Piece exakt auf das benötigte Format eines Kanals abgestimmt (umfasst Text und Bild, exklusive Video)`,
      'Human in the loop: Keine Veröffentlichung ohne dein "Go"',
      `Content Kalender: Vorplanung der Veröffentlichungen auf ${apiPlans.starter.social} Social Media Channel${apiPlans.starter.social === 1 ? '' : 's'} und ${apiPlans.starter.website} deiner lovable-Webseite${apiPlans.starter.website === 1 ? '' : 'n'}`,
      'Handy-App und Desktop',
      'Support per Chat',
    ] : [
      '1 brand',
      'Comprehensive brand analysis as the foundation for every content piece',
      `${fmtN(apiPlans.starter.credits)} credits for content creation and production: Each content piece tailored to the required format of a channel (includes text and image, exclusive video)`,
      'Human in the loop: No publication without your "Go"',
      `Content calendar: Plan publications across ${apiPlans.starter.social} social media channel${apiPlans.starter.social === 1 ? '' : 's'} and ${apiPlans.starter.website} of your lovable website${apiPlans.starter.website === 1 ? '' : 's'}`,
      'Mobile app and desktop',
      'Chat support',
    ],
    growth: lang === 'de' ? [
      '1 Marke',
      'Umfassende Markenanalyse als Grundlage jedes Content Pieces',
      `${fmtN(apiPlans.growth.credits)} Credits für die Kreation und Produktion von Content Pieces: Jedes Content Piece exakt auf das benötigte Format eines Kanals abgestimmt (umfasst Text und Bild und ${apiPlans.growth.videos} Video${apiPlans.growth.videos === 1 ? '' : 's'})`,
      'Human in the loop: Keine Veröffentlichung ohne dein "Go"',
      `Content Kalender: Vorplanung der Veröffentlichungen auf bis zu ${apiPlans.growth.social} Social Media Channels und bis zu ${apiPlans.growth.website} deiner lovable-Websiten`,
      'Platzierung einer Top Nachricht direkt in 8 Search Engines und 40 Presseportalen',
      'Handy-App und Desktop',
      'Support per Chat',
    ] : [
      '1 brand',
      'Comprehensive brand analysis as the foundation for every content piece',
      `${fmtN(apiPlans.growth.credits)} credits for content creation and production: Each content piece tailored to the required format of a channel (includes text and image and ${apiPlans.growth.videos} video${apiPlans.growth.videos === 1 ? '' : 's'})`,
      'Human in the loop: No publication without your "Go"',
      `Content calendar: Plan publications across up to ${apiPlans.growth.social} social media channels and up to ${apiPlans.growth.website} of your lovable websites`,
      'Top news placement directly in 8 search engines and 40 press portals',
      'Mobile app and desktop',
      'Chat support',
    ],
  };

  const customFeatures = lang === 'de' ? [
    'Individuelle Anzahl an Marken',
    'Umfassende Markenanalyse als Grundlage jedes Content Pieces',
    'Individuelle Anzahl an Credits für die Kreation und Produktion von Content Pieces: Jedes Content Piece exakt auf das benötigte Format eines Kanals abgestimmt (umfasst Text, Bild, Voice)',
    'Sharing Feature zum Content Austausch zwischen internationalen Marktpräsenzen',
    'Human in the loop: Keine Veröffentlichung ohne dein "Go"',
    'Content Kalender: Vorplanung der Veröffentlichungen auf einer individuellen Anzahl an Social Media Channels und lovable-Websiten',
    'Platzierung einer individuellen Anzahl an Top Nachrichten direkt in 8 Search Engines und 40 Presseportalen',
    'Handy-App und Desktop',
    'Support per Chat',
  ] : [
    'Individual number of brands',
    'Comprehensive brand analysis as the foundation for every content piece',
    'Individual number of credits for content creation and production: Each content piece tailored to the required format of a channel (includes text, image, voice)',
    'Sharing feature for content exchange between international market presences',
    'Human in the loop: No publication without your "Go"',
    'Content calendar: Plan publications across individual number of social media channels and lovable websites',
    'Top news placement directly in 8 search engines and 40 press portals',
    'Mobile app and desktop',
    'Chat support',
  ];

  // ============ FUNCTIONS ============
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitOrder = async () => {
    setLoading(true);
    setError(null);

    // Client-side validation — catch empty fields before hitting the backend
    const requiredFields = [
      ['companyName', 'Firmenname'],
      ['firstName', 'Vorname'],
      ['lastName', 'Nachname'],
      ['email', 'E-Mail'],
      ['street', 'Straße & Hausnummer'],
      ['city', 'Stadt'],
      ['zip', 'PLZ'],
      ['country', 'Land'],
    ];
    const missing = requiredFields
      .filter(([key]) => !String(formData[key] || '').trim())
      .map(([, label]) => label);
    if (missing.length > 0) {
      setError(
        (lang === 'de' ? 'Bitte fülle folgende Felder aus: ' : 'Please fill in: ') +
        missing.join(', ')
      );
      setLoading(false);
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      setError(lang === 'de' ? 'Bitte gib eine gültige E-Mail-Adresse ein.' : 'Please enter a valid email address.');
      setLoading(false);
      return;
    }

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

    try {
      const response = await fetch(`${BACKEND_URL}/api/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          company_name: formData.companyName,
          company_type: formData.companyType,
          tax_id: formData.taxId,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          street: formData.street,
          city: formData.city,
          zip: formData.zip,
          country: formData.country,
          plan: selectedPlan,
          billing_cycle: billingCycle,
          lang: lang || 'de',
        }),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        // FastAPI/Pydantic validation errors come as an ARRAY of objects under `detail`.
        // We flatten it to a readable string so the user doesn't see "[object Object]".
        let msg;
        const d = body?.detail;
        if (Array.isArray(d)) {
          msg = d.map(err => {
            const loc = Array.isArray(err.loc) ? err.loc.filter(p => p !== 'body').join('.') : '';
            return loc ? `${loc}: ${err.msg}` : err.msg;
          }).join(' • ');
        } else if (typeof d === 'string') {
          msg = d;
        } else if (typeof d === 'object' && d !== null) {
          msg = d.msg || JSON.stringify(d);
        } else {
          msg = body?.message || `Order submission failed (HTTP ${response.status})`;
        }
        throw new Error(msg);
      }

      setBillingStep(4);
      // Sidebar + gates pick up the new plan
      try { sub.refetch(); } catch { /* noop */ }
    } catch (err) {
      setError(err.message);
      console.error('Order submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const goToNextStep = () => {
    if (billingStep < 3) {
      setBillingStep(billingStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (billingStep > 0) {
      setBillingStep(billingStep - 1);
    }
  };

  // ============ JSX ============
  
  // Pricing View
  if (billingStep === 0 && selectedPlan === null) {
    return (
      <div className="min-h-screen bg-[#0a1628] text-white px-4 py-12">
        <div className="max-w-6xl mx-auto mb-16">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-4 text-[#00D9C8]">
              {t.trialEnd.title}
            </h1>
            <p className="text-lg text-gray-300">
              {t.trialEnd.subtitle}
              <br/>
              <span className="text-[#00D9C8] font-semibold">{t.trialEnd.cta}</span>
            </p>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-[#0f2440] rounded-full p-1 inline-flex border border-[#1a3a52]">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-[#00D9C8] text-[#0a1628]'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {t.trialEnd.monthly}
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  billingCycle === 'yearly'
                    ? 'bg-[#00D9C8] text-[#0a1628]'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {t.trialEnd.yearly} <span className="text-[#00D9C8] ml-1">({t.trialEnd.savings})</span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Starter */}
            <div className="bg-[#0f2440] border border-[#1a3a52] rounded-2xl p-8 hover:border-[#00D9C8]/50 transition-all">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">{plans[billingCycle].starter.name}</h3>
                <div className="text-4xl font-bold mb-2">
                  €{plans[billingCycle].starter.price}
                  <span className="text-base text-gray-400 font-normal"> /{t.trialEnd.perMonth}</span>
                </div>
                <p className="text-sm text-gray-400">{plans[billingCycle].starter.description}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {features.starter.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-[#00D9C8] mt-1 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  setSelectedPlan('starter');
                  setBillingStep(1);
                }}
                className="w-full bg-[#00D9C8] hover:bg-[#00c9b8] text-[#0a1628] font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
              >
                {t.trialEnd.orderNow}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Growth (Featured) */}
            <div className="relative md:scale-105 md:z-10">
              <div className="absolute -inset-1 bg-[#00D9C8]/20 rounded-2xl blur-lg"></div>
              <div className="bg-[#0f2440] border border-[#00D9C8] rounded-2xl p-8 relative">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#00D9C8] text-[#0a1628] px-4 py-1 rounded-full text-xs font-bold">
                  {t.trialEnd.featured}
                </div>

                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-2">{plans[billingCycle].growth.name}</h3>
                  <div className="text-4xl font-bold mb-2">
                    €{plans[billingCycle].growth.price}
                    <span className="text-base text-gray-400 font-normal"> /{t.trialEnd.perMonth}</span>
                  </div>
                  <p className="text-sm text-[#00D9C8]">{plans[billingCycle].growth.description}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {features.growth.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-200">
                      <Check className="w-4 h-4 text-[#00D9C8] mt-1 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => {
                    setSelectedPlan('growth');
                    setBillingStep(1);
                  }}
                  className="w-full bg-[#00D9C8] hover:bg-[#00c9b8] text-[#0a1628] font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
                >
                  {t.trialEnd.orderNow}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Custom */}
            <div className="bg-[#0f2440] border border-[#1a3a52] rounded-2xl p-8 hover:border-[#00D9C8]/50 transition-all">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Custom</h3>
                <div className="text-4xl font-bold mb-2">
                  {lang === 'de' ? 'Individuell' : 'Custom'}
                </div>
                <p className="text-sm text-gray-400">{t.trialEnd.customDesc}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {customFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-[#00D9C8] mt-1 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href="https://calendly.com/isabell-xchannels/30min?month=2026-04"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#1a3a52] hover:bg-[#254465] text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
              >
                {t.trialEnd.bookCall}
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="bg-[#0f2440] border border-[#1a3a52] rounded-2xl p-8 text-center">
            <p className="text-lg text-gray-300 mb-4">
              {t.trialEnd.discussion}
            </p>
            <a
              href="https://calendly.com/isabell-xchannels/30min?month=2026-04"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#1a3a52] hover:bg-[#254465] text-white font-bold px-6 py-3 rounded-lg transition-all"
            >
              {t.trialEnd.bookCall}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Billing Flow - Company Step
  if (billingStep === 1) {
    return (
      <div className="min-h-screen bg-[#0a1628] text-white px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">{t.trialEnd.billingData}</h1>
            <p className="text-gray-400">{t.trialEnd.step1Of3}</p>
            <div className="w-full bg-[#1a3a52] rounded-full h-1 mt-4">
              <div className="bg-[#00D9C8] h-1 rounded-full" style={{ width: '33%' }}></div>
            </div>
          </div>

          <div className="bg-[#0f2440] border border-[#1a3a52] rounded-2xl p-8 mb-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">{t.trialEnd.companyName}</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder={lang === 'de' ? 'Dein Firmenname' : 'Your company name'}
                  className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#00D9C8] focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">{t.trialEnd.companyType}</label>
                <select
                  value={formData.companyType}
                  onChange={(e) => handleInputChange('companyType', e.target.value)}
                  className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white focus:border-[#00D9C8] focus:outline-none transition-colors"
                >
                  <option value="">{lang === 'de' ? '-- Bitte wählen --' : '-- Please select --'}</option>
                  <option value="sole_trader">{t.trialEnd.soleTrader}</option>
                  <option value="limited_company">{t.trialEnd.limitedCompany}</option>
                </select>
              </div>

              {formData.companyType && (
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    {formData.companyType === 'sole_trader' ? t.trialEnd.taxNumber : t.trialEnd.vatId}
                  </label>
                  <input
                    type="text"
                    value={formData.taxId}
                    onChange={(e) => handleInputChange('taxId', e.target.value)}
                    placeholder={formData.companyType === 'sole_trader' ? 'z.B. 12 345 678 901' : 'z.B. DE 123456789'}
                    className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#00D9C8] focus:outline-none transition-colors"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between gap-4">
            <button
              onClick={() => {
                setSelectedPlan(null);
                setBillingStep(0);
              }}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {t.trialEnd.back}
            </button>
            <button
              onClick={goToNextStep}
              disabled={!formData.companyName || !formData.companyType || !formData.taxId}
              className="bg-[#00D9C8] hover:bg-[#00c9b8] disabled:opacity-50 disabled:cursor-not-allowed text-[#0a1628] font-bold px-6 py-3 rounded-lg transition-all flex items-center gap-2"
            >
              {t.trialEnd.next}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Billing Flow - Contact Step
  if (billingStep === 2) {
    return (
      <div className="min-h-screen bg-[#0a1628] text-white px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">{t.trialEnd.billingData}</h1>
            <p className="text-gray-400">{t.trialEnd.step2Of3}</p>
            <div className="w-full bg-[#1a3a52] rounded-full h-1 mt-4">
              <div className="bg-[#00D9C8] h-1 rounded-full" style={{ width: '66%' }}></div>
            </div>
          </div>

          <div className="bg-[#0f2440] border border-[#1a3a52] rounded-2xl p-8 mb-8">
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">{t.trialEnd.firstName}</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder={lang === 'de' ? 'Vorname' : 'First name'}
                    className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#00D9C8] focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t.trialEnd.lastName}</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder={lang === 'de' ? 'Nachname' : 'Last name'}
                    className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#00D9C8] focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">{t.trialEnd.email}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder={lang === 'de' ? 'deine@email.com' : 'your@email.com'}
                  className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#00D9C8] focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">{t.trialEnd.phone}</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+49 123 456789"
                  className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#00D9C8] focus:outline-none transition-colors"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between gap-4">
            <button
              onClick={goToPreviousStep}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {t.trialEnd.back}
            </button>
            <button
              onClick={goToNextStep}
              disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phone}
              className="bg-[#00D9C8] hover:bg-[#00c9b8] disabled:opacity-50 disabled:cursor-not-allowed text-[#0a1628] font-bold px-6 py-3 rounded-lg transition-all flex items-center gap-2"
            >
              {t.trialEnd.next}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Billing Flow - Address Step
  if (billingStep === 3) {
    return (
      <div className="min-h-screen bg-[#0a1628] text-white px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">{t.trialEnd.billingData}</h1>
            <p className="text-gray-400">{t.trialEnd.step3Of3}</p>
            <div className="w-full bg-[#1a3a52] rounded-full h-1 mt-4">
              <div className="bg-[#00D9C8] h-1 rounded-full" style={{ width: '100%' }}></div>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-8">
              <p className="text-red-200">{error}</p>
            </div>
          )}

          <div className="bg-[#0f2440] border border-[#1a3a52] rounded-2xl p-8 mb-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">{t.trialEnd.street}</label>
                <input
                  type="text"
                  value={formData.street}
                  onChange={(e) => handleInputChange('street', e.target.value)}
                  placeholder={lang === 'de' ? 'Musterstraße 123' : 'Example street 123'}
                  className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#00D9C8] focus:outline-none transition-colors"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-2">{t.trialEnd.city}</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder={lang === 'de' ? 'Berlin' : 'Berlin'}
                    className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#00D9C8] focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t.trialEnd.zip}</label>
                  <input
                    type="text"
                    value={formData.zip}
                    onChange={(e) => handleInputChange('zip', e.target.value)}
                    placeholder="10115"
                    className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#00D9C8] focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">{t.trialEnd.country}</label>
                <select
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full bg-[#1a3a52] border border-[#2a4a62] rounded-lg px-4 py-3 text-white focus:border-[#00D9C8] focus:outline-none transition-colors"
                >
                  <option value="DE">{lang === 'de' ? 'Deutschland' : 'Germany'}</option>
                  <option value="AT">{lang === 'de' ? 'Österreich' : 'Austria'}</option>
                  <option value="CH">{lang === 'de' ? 'Schweiz' : 'Switzerland'}</option>
                  <option value="FR">{lang === 'de' ? 'Frankreich' : 'France'}</option>
                  <option value="ES">{lang === 'de' ? 'Spanien' : 'Spain'}</option>
                  <option value="PL">{lang === 'de' ? 'Polen' : 'Poland'}</option>
                  <option value="CZ">{lang === 'de' ? 'Tschechien' : 'Czech Republic'}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-[#00D9C8]/10 border border-[#00D9C8]/30 rounded-2xl p-8 mb-8">
            <h3 className="font-bold text-lg mb-4">{t.trialEnd.summary}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>{t.trialEnd.plan}:</span>
                <span className="font-bold">
                  {selectedPlan === 'starter' ? 'Starter' : 'Growth'} ({billingCycle === 'monthly' ? t.trialEnd.monthly : t.trialEnd.yearly})
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t.trialEnd.price}:</span>
                <span className="font-bold text-[#00D9C8]">
                  €{billingCycle === 'monthly' 
                    ? (selectedPlan === 'starter' ? 99 : 229) 
                    : (selectedPlan === 'starter' ? 79 : 199)
                  }
                  <span className="text-xs text-gray-400 font-normal"> /{t.trialEnd.perMonth}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-between gap-4">
            <button
              onClick={goToPreviousStep}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {t.trialEnd.back}
            </button>
            <button
              onClick={handleSubmitOrder}
              disabled={!formData.street || !formData.city || !formData.zip || loading}
              className="bg-[#00D9C8] hover:bg-[#00c9b8] disabled:opacity-50 disabled:cursor-not-allowed text-[#0a1628] font-bold px-6 py-3 rounded-lg transition-all flex items-center gap-2"
            >
              {loading ? 'Loading...' : t.trialEnd.getInvoice}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success Step
  if (billingStep === 4) {
    return (
      <div className="min-h-screen bg-[#0a1628] text-white px-4 py-12 flex items-center justify-center">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00D9C8] rounded-full mb-6">
            <Check className="w-8 h-8 text-[#0a1628]" />
          </div>
          
          <h1 className="text-5xl font-bold mb-4 text-[#00D9C8]">
            {t.trialEnd.thankyou}
          </h1>
          
          <p className="text-lg text-gray-300 mb-8">
            {t.trialEnd.successMessage}
          </p>

          <div className="bg-[#0f2440] border border-[#1a3a52] rounded-2xl p-8 mb-8 text-left">
            <h3 className="font-bold text-lg mb-4">{t.trialEnd.whatNext}</h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#00D9C8] mt-0.5 flex-shrink-0" />
                <span>{t.trialEnd.confirmation}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#00D9C8] mt-0.5 flex-shrink-0" />
                <span>{t.trialEnd.creditsAvailable}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#00D9C8] mt-0.5 flex-shrink-0" />
                <span>{t.trialEnd.fullAccess}</span>
              </li>
            </ul>
          </div>

          <a
            href="/app/kreation"
            className="inline-block bg-[#00D9C8] hover:bg-[#00c9b8] text-[#0a1628] font-bold px-8 py-4 rounded-lg transition-all"
          >
            {t.trialEnd.backToApp}
          </a>
        </div>
      </div>
    );
  }
};

export default TrialEnd;
